import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, RefreshCw, ShieldAlert } from "lucide-react";
import { highRiskActionLabels, type ApprovalOperationType, type HighRiskAction, type SessionView } from "@control-plane/contracts";
import { api } from "../lib/api";
import { Dialog } from "./Overlay";
import { AiExplainError } from "./AssistantInlineHelp";

/**
 * P0 审批闭环的统一入口(AC-SEC-001 / RBAC-002)。
 *
 * 业务页的高风险操作统一打开本组件。普通管理员执行双人审批；platform_owner
 * 对租户生命周期可确认原因后直接执行，其他高风险操作仍执行双人审批:
 *   1. 从具体业务对象发起申请(操作内容 payload 与执行端逐字段一致);
 *   2. 另一管理员在「企业治理 → 审批工作台」批准;
 *   3. 申请人回到业务页,从「可执行的已批准申请」中按业务名称选择并执行;
 *   4. 执行成功后由业务页回读结果。
 *
 * 普通用户全程不接触 UUID / actionType / targetType / targetId / approvalId,
 * 这些技术字段只存在于组件内部与请求体中。
 */
export type ApprovalOperation = {
  action: HighRiskAction;
  targetType: string;
  targetId: string;
  tenantId: string | null;
  /** 与后端 approval-payload.ts 一致的规范化操作内容;申请与执行共用同一份。 */
  payload: Record<string, unknown>;
};

type ApprovalItem = {
  id: string;
  actionType: string;
  operationType?: ApprovalOperationType;
  targetType: string;
  targetId: string;
  target?: Record<string, string>;
  tenantId: string | null;
  targetLabel: string;
  reason: string;
  status: string;
  payload: Record<string, unknown>;
  requestedBy: string;
  requestedByName: string;
  approvedBy?: string | null;
  decidedBy?: string | null;
  createdAt: string;
  executedAt: string | null;
  expiresAt: string;
};

/** 与后端 canonicalJson 相同的稳定键序序列化,用于前端筛选可执行申请。 */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

async function sha256Hex(value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error("当前浏览器不支持本地生成审批摘要，请升级浏览器后重试。");
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function approvalSecretDigest(value: unknown): Promise<string> {
  return `sha256:${await sha256Hex(canonicalJson(value))}`;
}

export async function credentialRotateApprovalPayload(input: {
  credentialId: string;
  scope: "platform" | "tenant";
  tenantId: string | null;
  provider: string;
  submittedSecretFields: string[];
  secretDigestInput: unknown;
}): Promise<Record<string, unknown>> {
  return {
    op: "rotate",
    credentialId: input.credentialId,
    scope: input.scope,
    tenantId: input.tenantId,
    provider: input.provider,
    submittedSecretFields: [...new Set(input.submittedSecretFields)].sort(),
    secretPayloadDigest: await approvalSecretDigest(input.secretDigestInput),
  };
}

function approvalOperationType(operation: ApprovalOperation): ApprovalOperationType {
  if (operation.action === "tenant.lifecycle") {
    const status = operation.payload.targetStatus;
    if (status === "active") return "tenant.activate";
    if (status === "suspended") return "tenant.suspend";
    if (status === "purged") return "tenant.purge";
    return "tenant.close";
  }
  if (operation.action === "admin.manage") {
    if (operation.payload.op === "create") return "admin.create";
    if (operation.payload.op === "delete") return "admin.delete";
    return "admin.update";
  }
  if (operation.action === "plans.publish") return operation.targetType === "addon_version" ? "addon.publish" : "plan.publish";
  if (operation.action === "subscriptions.change") return operation.payload.changeType === "force_migrate" ? "plan.force_migration" : "subscription.change";
  if (operation.action === "orders.create") return "discount_rule.approve";
  if (operation.action === "payments.confirm_offline" || operation.action === "orders.confirm_payment") return "payment.offline_confirm";
  if (operation.action === "invoices.adjust") return "invoice.adjust";
  if (operation.action === "refunds.approve") return "refund.approve";
  if (operation.action === "refunds.execute") return "refund.execute";
  if (operation.action === "usage.adjust") return "usage.adjust";
  if (operation.action === "overrides.manage") return "quota_override.create";
  if (operation.action === "credential.rotate") return "credential.rotate";
  if (operation.action === "credential.revoke") return "credential.revoke";
  if (operation.action === "credential.rollback") return "credential.rollback";
  if (operation.action === "support.approve") return "support.approve";
  if (operation.action === "feature_flags.manage") {
    return operation.payload.enabled === true && operation.payload.rolloutPercent === 100 ? "feature_flag.full_publish" : "feature_flag.adjust";
  }
  throw new Error(`未支持的审批操作：${operation.action}`);
}

function approvalTarget(operation: ApprovalOperation, operationType: ApprovalOperationType): Record<string, string> {
  if (operationType.startsWith("tenant.")) return { tenantId: operation.targetId };
  if (operationType === "admin.create") return { adminEmail: operation.targetId };
  if (operationType === "admin.update" || operationType === "admin.delete") return { adminId: operation.targetId };
  if (operationType === "plan.publish") return { planVersionId: operation.targetId };
  if (operationType === "addon.publish") return { addonVersionId: operation.targetId };
  if (operationType === "plan.force_migration" || operationType === "subscription.change") return { subscriptionChangeId: operation.targetId };
  if (operationType === "discount_rule.approve") return { discountRuleId: operation.targetId };
  if (operationType === "payment.offline_confirm") return { purchaseOrderId: operation.targetId };
  if (operationType === "invoice.adjust") return { invoiceId: operation.targetId };
  if (operationType === "refund.approve" || operationType === "refund.execute") return { refundId: operation.targetId };
  if (operationType === "usage.adjust" || operationType === "quota_override.create") return { usageMeterId: operation.targetId };
  if (operationType === "credential.rotate" || operationType === "credential.revoke" || operationType === "credential.rollback") return { credentialId: operation.targetId };
  if (operationType === "support.approve") return { supportGrantId: operation.targetId };
  return { featureFlagKey: operation.targetId };
}

function approvalPayload(operation: ApprovalOperation, operationType: ApprovalOperationType): Record<string, unknown> {
  if (operation.action === "feature_flags.manage") {
    const previousRolloutPercent = typeof operation.payload.previousRolloutPercent === "number" ? operation.payload.previousRolloutPercent : 0;
    if (operationType === "feature_flag.full_publish") {
      return {
        operation: "full_publish",
        enabled: true,
        rolloutPercent: 100,
        previousRolloutPercent,
        impactScope: operation.tenantId ? "tenant" : "all_targets",
        acknowledgedFullReleaseImpact: operation.payload.acknowledgedFullReleaseImpact !== false,
      };
    }
    return {
      operation: "adjust",
      enabled: operation.payload.enabled,
      rolloutPercent: operation.payload.rolloutPercent,
      previousRolloutPercent,
    };
  }
  if (operationType === "credential.rotate") {
    return {
      op: "rotate",
      credentialId: operation.targetId,
      scope: operation.payload.scope,
      tenantId: operation.tenantId,
      provider: operation.payload.provider,
      submittedSecretFields: operation.payload.submittedSecretFields,
      secretPayloadDigest: operation.payload.secretPayloadDigest,
    };
  }
  return operation.payload;
}

function approvalRequestPayload(operation: ApprovalOperation, operationType: ApprovalOperationType): Record<string, unknown> {
  const payload = approvalPayload(operation, operationType);
  if (operation.action === "feature_flags.manage") return { ...payload, tenantId: operation.tenantId ?? undefined };
  return payload;
}

export function ApprovalGateModal({ session, operation, title, targetLabel, impact, recovery, execute, onClose, onExecuted }: {
  session: SessionView;
  operation: ApprovalOperation;
  title: string;
  targetLabel: string;
  impact: string[];
  recovery: string;
  /** 携带批准凭证执行真实 mutation;reason 复用所选申请的申请原因。 */
  execute: (approvalId: string, reason: string) => Promise<void>;
  onClose: () => void;
  onExecuted: () => void;
}) {
  const [items, setItems] = useState<ApprovalItem[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const operationType = approvalOperationType(operation);
  const platformOwnerOverride = session.admin.role === "platform_owner" && operation.action === "tenant.lifecycle";
  const requestTarget = approvalTarget(operation, operationType);
  const requestPayload = approvalPayload(operation, operationType);
  const load = useCallback(async () => {
    setLoadError("");
    try {
      const data = await api<{ items: ApprovalItem[] }>("/api/approvals");
      setItems(data.items);
    } catch (failure) {
      setLoadError(failure instanceof Error ? failure.message : "审批记录读取失败");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const candidates = (items ?? []).filter((item) => {
    const approverId = item.approvedBy ?? item.decidedBy ?? null;
    return item.actionType === operation.action &&
      item.targetType === operation.targetType &&
      item.targetId === operation.targetId &&
      (item.tenantId ?? null) === operation.tenantId &&
      item.status === "approved" &&
      item.executedAt === null &&
      new Date(item.expiresAt).getTime() > Date.now() &&
      item.requestedBy === session.admin.id &&
      (!approverId || approverId !== session.admin.id) &&
      (
        canonicalJson(item.payload) === canonicalJson(requestPayload) ||
        ((operationType === "subscription.change" || operationType === "plan.force_migration") &&
          item.operationType === operationType &&
          canonicalJson(item.target ?? {}) === canonicalJson(requestTarget))
      );
  });
  const selected = candidates.find((item) => item.id === selectedId) ?? null;
  async function submitRequest() {
    setBusy(true); setError("");
    try {
      await api("/api/approvals", { method: "POST", body: JSON.stringify({
        operationType,
        target: requestTarget,
        reason,
        payload: approvalRequestPayload(operation, operationType),
        expiresAt: new Date(Date.now() + 3_600_000).toISOString(),
        idempotencyKey: `approval-${operationType}-${crypto.randomUUID()}`,
      })});
      setNotice("申请已提交。请另一名有权限的管理员在「企业治理 → 审批工作台」批准;批准后回到本页选择该申请执行。");
      setReason("");
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "审批申请提交失败");
    } finally {
      setBusy(false);
    }
  }
  async function run() {
    if (!selected) return;
    setBusy(true); setError("");
    try {
      await execute(selected.id, selected.reason);
      onExecuted();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "执行失败,批准凭证未被消费,可重试");
    } finally {
      setBusy(false);
    }
  }
  async function runAsPlatformOwner() {
    if (reason.trim().length < 8) return;
    setBusy(true); setError("");
    try {
      await execute("", reason.trim());
      onExecuted();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "执行失败，请核对当前状态后重试");
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={title} description={`影响对象:${targetLabel} · ${highRiskActionLabels[operation.action] ?? "高危操作（待确认）"}`} onClose={onClose}>
    <div className="approval-gate">
      <div className="approval-gate-brief">
        <ShieldAlert size={18} />
        <div>
          <strong>执行影响</strong>
          <ul>{impact.map((line) => <li key={line}>{line}</li>)}</ul>
          <span>恢复方式:{recovery}</span>
          <span>{platformOwnerOverride ? "平台负责人确认操作原因后可立即执行，结果以页面回读为准。" : "下一步:提交申请后由另一名管理员批准;批准后回到本页,在「可执行的已批准申请」中选择并执行,结果以页面回读为准。"}</span>
        </div>
      </div>
      {platformOwnerOverride ? <section className="approval-gate-section">
        <h4>平台负责人直接执行</h4>
        <label className="approval-gate-reason">操作原因（至少 8 个字，会写入审计）<textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明本次操作目的和确认依据" /></label>
        <button className="button primary" disabled={busy || reason.trim().length < 8} onClick={() => void runAsPlatformOwner()}>{busy ? "正在执行…" : "确认并立即执行"}</button>
      </section> : <>
      {loadError
        ? <div className="form-error inline-feedback"><span>{loadError}</span><button className="button ghost" onClick={() => void load()}><RefreshCw size={14} />重试</button></div>
        : items === null
          ? <p className="approval-gate-loading">正在读取审批记录…</p>
          : <>
            {candidates.length > 0 && <section className="approval-gate-section">
              <h4>可执行的已批准申请</h4>
              <div className="approval-gate-list">
                {candidates.map((item) => (
                  <label key={item.id} className={selectedId === item.id ? "selected" : ""}>
                    <input type="radio" name="approval-gate-choice" checked={selectedId === item.id} onChange={() => setSelectedId(item.id)} />
                    <span><strong>{item.reason}</strong><small>申请于 {new Date(item.createdAt).toLocaleString("zh-CN")}</small></span>
                    <BadgeCheck size={16} />
                  </label>
                ))}
              </div>
              <button className="button primary" disabled={!selected || busy} onClick={() => void run()}>{busy ? "正在执行…" : "立即执行"}</button>
            </section>}
            <section className="approval-gate-section">
              <h4>发起审批申请</h4>
              {notice && <p className="approval-gate-notice">{notice}</p>}
              <label className="approval-gate-reason">申请原因(至少 12 个字,会写入审计)<textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明已确认的事实和本次操作目的" /></label>
              <button className="button secondary" disabled={busy || reason.trim().length < 12} onClick={() => void submitRequest()}>{busy ? "正在提交…" : "提交审批申请"}</button>
            </section>
          </>}
      </>}
      {error && <div className="form-error inline-feedback"><span>{error}</span><AiExplainError message={error} context={title} /></div>}
    </div>
  </Dialog>;
}
